import React, { useState, useEffect, useRef } from "react";
import Header from "../header/header";
import Footer from "../footer/footer";
import NavBar from "../nav/nav-bar";
import ReportNav from "./report-nav";
import { useHistory, useParams } from "react-router-dom";
import * as echarts from "echarts";
import Service from "../webservice/http";
import ClipLoader from "react-spinners/ClipLoader";
import "./report-dashboard.css";
const THEMES = {
  default: [
    "#271078",
    "#e9be73",
    "#df6439",
    "#e25858",
    "#3b82f6",
    "#10b981",
    "#8b5cf6",
    "#ec4899",
    "#14b8a6",
    "#f59e0b",
  ],
  ocean: [
    "#0077b6",
    "#00b4d8",
    "#90e0ef",
    "#03045e",
    "#48cae4",
    "#0096c7",
    "#023e8a",
    "#ade8f4",
  ],
  sunset: [
    "#ff5a00",
    "#ff9a00",
    "#ffce00",
    "#b23a00",
    "#5c1000",
    "#2a0800",
    "#e2703a",
    "#c24d2c",
  ],
  forest: [
    "#2d6a4f",
    "#40916c",
    "#52b788",
    "#1b4332",
    "#74c69d",
    "#95d5b2",
    "#132a13",
    "#31572c",
  ],
  purple: [
    "#6800b3",
    "#8600e6",
    "#a11aff",
    "#4a0080",
    "#bd4dff",
    "#d980ff",
    "#2e004f",
    "#f5b3ff",
  ],
  pastel: [
    "#ffb3ba",
    "#ffdfba",
    "#ffffba",
    "#baffc9",
    "#bae1ff",
    "#e8baff",
    "#fcc2ff",
  ],
  neon: [
    "#ff00ff",
    "#00ffff",
    "#39ff14",
    "#ffff00",
    "#ff3131",
    "#1f51ff",
    "#8a2be2",
  ],
  monochrome: [
    "#1c1c1c",
    "#383838",
    "#555555",
    "#717171",
    "#8d8d8d",
    "#aaaaaa",
    "#c6c6c6",
  ],
  corporate: [
    "#1e3d59",
    "#f5f0e1",
    "#ff6e40",
    "#ffc13b",
    "#17252a",
    "#3aafa9",
    "#def2f1",
  ],
  tropical: [
    "#ff0054",
    "#ffbd00",
    "#ff5400",
    "#390099",
    "#9e0059",
    "#0ea5e9",
    "#00f5d4",
  ],
};

function formatDateLabel(d, type) {
  if (typeof d === "string") {
    const parts = d.split("-");
    if (parts.length === 3 && parts[0].length === 2) {
      d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T12:00:00Z`);
    } else {
      d = new Date(d);
    }
  }
  if (!d || isNaN(d.getTime())) return String(d);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = d.getDate();
  if (type === "day") return `${y}-${m}-${String(day).padStart(2, "0")}`;
  if (type === "week") {
    const w = new Date(d);
    w.setDate(d.getDate() - d.getDay());
    return `Wk ${w.getFullYear()}-${String(w.getMonth() + 1).padStart(2, "0")}-${String(w.getDate()).padStart(2, "0")}`;
  }
  if (type === "15days")
    return day <= 15 ? `${y}-${m}-1stHalf` : `${y}-${m}-2ndHalf`;
  if (type === "quarter") return `${y}-Q${Math.ceil((d.getMonth() + 1) / 3)}`;
  if (type === "6months") return d.getMonth() < 6 ? `${y}-H1` : `${y}-H2`;
  if (type === "year") return `${y}`;
  return `${y}-${m}`;
}

const formatAxisLabel = (str) => {
  if (!str || str === "none") return "";
  return str
    .replace(/_/g, " ")
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .replace(/\b\w/g, (l) => l.toUpperCase());
};

function groupData(data, dimKey, metricKey, agg, timeGroup) {
  const dimFn = (r) => {
    if (dimKey === "call_date" || dimKey === "audit_date") {
      return formatDateLabel(r[dimKey] || r.call_date, timeGroup || "month");
    }
    if (dimKey === "status" || dimKey === "call_collection_status") {
      return r.status || r.collection_status || "Unknown";
    }
    if (dimKey === "ptp_status") {
      return r.ptp_status === true || r.ptp_count > 0 ? "PTP" : "No PTP";
    }
    if (dimKey === "kept_status") {
      return r.kept_status === true || r.kept_count > 0 ? "Kept" : "Not Kept";
    }
    const val = r[dimKey];
    return val === "undefined" ||
      val === null ||
      val === undefined ||
      val === ""
      ? formatAxisLabel(dimKey)
      : val;
  };

  const metricFn =
    {
      count: () => 1,
      collected_amount: (r) => r.collected_amount,
      target_amount: (r) => r.target_amount,
      due_age: (r) => r.due_age,
      collection_score: (r) => r.collection_score,
      settlement_score: (r) => r.settlement_score,
      total_points: (r) => r.total_points,
      ptp_count: (r) => r.ptp_count,
      kept_count: (r) => r.kept_count,
      cx_score: (r) => r.cx_score,
      agent: (r) => r.agent,
      customer: (r) => r.customer,
      qa_score: (r) => r.qa_total_points || 0,
      aht: (r) => r.aht_score || 0,
      aht_score: (r) => r.aht_score || 0,
      rpc: (r) => r.rpc || 0,
      calls_handled: (r) => r.calls_handled || 0,
      customer_sentiment: (r) => r.cx_score || 0,
      agent_sentiment: (r) => r.cx_score || 0,
      ptp_generated: (r) => r.ptp_count || 0,
      kept_rate: (r) =>
        r.kept_count && r.ptp_count ? (r.kept_count / r.ptp_count) * 100 : 0,
      collection_strategy_points: (r) => r.total_points || 0,
    }[metricKey] ||
    ((r) => {
      const val = r[metricKey];
      return typeof val === "number" ? val : 1;
    });

  const groups = {};
  data.forEach((r) => {
    const k = dimFn(r);
    if (!groups[k]) groups[k] = [];
    groups[k].push(metricFn(r));
  });

  const rows = Object.entries(groups).map(([k, vals]) => {
    const numericVals = vals.filter((v) => typeof v === "number");
    const sum = numericVals.reduce((a, b) => a + b, 0);
    let val = 0;
    if (agg === "sum") val = sum;
    else if (agg === "avg")
      val = numericVals.length ? sum / numericVals.length : 0;
    else if (agg === "count") val = vals.length;
    else if (agg === "distinct") val = new Set(vals).size;
    else if (agg === "max")
      val = numericVals.length ? Math.max(...numericVals) : 0;
    else if (agg === "min")
      val = numericVals.length ? Math.min(...numericVals) : 0;
    else if (agg === "median") {
      if (!numericVals.length) val = 0;
      else {
        const sorted = [...numericVals].sort((a, b) => a - b);
        const mid = Math.floor(sorted.length / 2);
        val =
          sorted.length % 2 !== 0
            ? sorted[mid]
            : (sorted[mid - 1] + sorted[mid]) / 2;
      }
    } else val = vals[0] || 0;
    return { k, v: parseFloat(val.toFixed(2)) };
  });

  rows.sort((a, b) => b.v - a.v);
  return rows;
}

function Savedreportdetail() {
  const history = useHistory();
  const service = new Service();
  const { id } = useParams();
  const [report, setReport] = useState([]);
  const [Loading, setLoading] = useState(false);

  useEffect(() => {
    setLoading(true);
    try {
      const fetchData = async () => {
        setLoading(true);
        const response = await service.get(
          `api/call_verification/save_report/?report_id=${id}`,
        );
        console.log("Saved Report API Response:", response);
        setReport(response);
        setLoading(false);
      };

      fetchData();
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  }, []);

  const chartRef = useRef(null);

  useEffect(() => {
    if (Loading || !report || !report.data || !chartRef.current) return;
    const chartEl = chartRef.current;
    const rnd = (min, max) => Math.floor(Math.random() * (max - min + 1)) + min;

    const cfg = report.filters?.chart_config || report.data?.chart_config || {};
    const chartType = cfg.chart_type || report.data.chart_type || "bar";
    const xKey = cfg.x_axis || report.data.x_axis || "call_date";
    const yKey = cfg.y_axis || report.data.y_axis || "count";
    const agg = cfg.aggregation || report.data.aggregation || "count";
    const timeGrp = cfg.time_grouping || report.data.time_grouping || "month";
    const themeKey = cfg.color_theme || "default";

    const yLabel =
      report.data?.y_axis_label ||
      report.data?.series_data?.metric_label ||
      formatAxisLabel(yKey);
    const xLabel = report.data?.x_axis_label || formatAxisLabel(xKey);
    const colors = THEMES[themeKey] || THEMES.default;
    const filteredData = report.data.call_records || [];

    const getVal = (r, key) => {
      if (!key || key === "none") return "Unknown";
      if (key === "call_date" || key === "audit_date")
        return formatDateLabel(r[key] || r.call_date, timeGrp || "month");
      if (key === "call_collection_status" || key === "status")
        return r.status || r.collection_status || "Unknown";
      if (key === "ptp_status")
        return r.ptp_status === true || r.ptp_count > 0 ? "PTP" : "No PTP";
      if (key === "kept_status")
        return r.kept_status === true || r.kept_count > 0 ? "Kept" : "Not Kept";
      if (
        r[key] === "undefined" ||
        r[key] === null ||
        r[key] === undefined ||
        r[key] === ""
      )
        return "Unknown";
      return r[key] || "Unknown";
    };

    const grouped = groupData(filteredData, xKey, yKey, agg, timeGrp);
    const labels = grouped.map((r) => r.k);
    const values = grouped.map((r) => r.v);

    const gridLineClr = "#edebe9";
    const textClr = "#646464";

    const chart = echarts.init(chartEl, null, { renderer: "canvas" });

    const baseConfig = {
      backgroundColor: "transparent",
      textStyle: {
        color: textClr,
        fontFamily: "Poppins, Inter, sans-serif",
        fontSize: 11,
      },
      grid: { top: 32, bottom: 40, left: 56, right: 16, containLabel: true },
      tooltip: {
        trigger: "axis",
        backgroundColor: "#ffffff",
        borderColor: "#e1dfdd",
        textStyle: { color: "#232323", fontSize: 11 },
        extraCssText:
          "box-shadow: 0 4px 12px rgba(0,0,0,0.15); border-radius: 4px;",
      },
      legend: {
        textStyle: { color: textClr, fontSize: 10 },
        icon: "circle",
        bottom: 0,
        type: "scroll",
        itemGap: 30,
        padding: [10, 0, 0, 0],
      },
    };

    let option = {};

    if (chartType === "pie" || chartType === "donut") {
      option = {
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: {
          bottom: 0,
          type: "scroll",
          itemGap: 30,
          padding: [10, 0, 0, 0],
        },
        series: [
          {
            type: "pie",
            radius: chartType === "donut" ? ["40%", "70%"] : "65%",
            data: grouped.slice(0, 15).map((r, i) => ({
              name: r.k,
              value: r.v,
              itemStyle: { color: colors[i % colors.length] },
            })),
          },
        ],
      };
    } else if (chartType === "treemap") {
      option = {
        series: [
          {
            type: "treemap",
            width: "100%",
            height: "100%",
            breadcrumb: { show: false },
            data: grouped.slice(0, 20).map((r, i) => ({
              name: r.k,
              value: r.v,
              itemStyle: { color: colors[i % colors.length] },
            })),
          },
        ],
      };
    } else if (chartType === "funnel") {
      option = {
        series: [
          {
            type: "funnel",
            left: "15%",
            width: "70%",
            data: grouped.slice(0, 8).map((r, i) => ({
              name: r.k,
              value: r.v,
              itemStyle: { color: colors[i % colors.length] },
            })),
          },
        ],
      };
    } else if (chartType === "radar") {
      const top6 = grouped.slice(0, 6);
      option = {
        radar: {
          indicator: top6.map((r) => ({
            name: r.k,
            max: Math.max(...values, 1) * 1.15,
          })),
          splitArea: { show: false },
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        series: [
          {
            type: "radar",
            data: [
              {
                value: top6.map((r) => r.v),
                name: formatAxisLabel(yKey),
                areaStyle: { color: "rgba(39, 16, 120, 0.2)" },
                lineStyle: { color: "#271078", width: 2 },
              },
            ],
          },
        ],
      };
    } else if (chartType === "heatmap") {
      const xLabels = [...new Set(filteredData.map((r) => r.agent))].slice(
        0,
        8,
      );
      const yLabels = [...new Set(filteredData.map((r) => r.product))].slice(
        0,
        6,
      );
      const cells = [];
      xLabels.forEach((x, xi) =>
        yLabels.forEach((y, yi) => {
          const matching = filteredData.filter(
            (r) => r.agent === x && r.product === y,
          );
          const val = matching.length
            ? parseFloat(
                (
                  matching.reduce((s, r) => s + r.collection_score, 0) /
                  matching.length
                ).toFixed(1),
              )
            : 0;
          cells.push([xi, yi, val]);
        }),
      );
      option = {
        xAxis: {
          type: "category",
          name: xLabel,
          nameLocation: "middle",
          nameGap: 30,
          data: xLabels,
          axisLabel: { rotate: 45, hideOverlap: true },
        },
        yAxis: { type: "category", data: yLabels },
        visualMap: {
          min: -50,
          max: 100,
          calculable: true,
          orient: "horizontal",
          left: "center",
          bottom: 0,
          inRange: { color: ["#ef4444", "#f59e0b", "#10b981"] },
        },
        series: [
          {
            type: "heatmap",
            data: cells,
            label: { show: true, fontSize: 9 },
          },
        ],
      };
    } else if (chartType === "scatter" || chartType === "bubble") {
      const sample = filteredData.slice(0, 200);
      const scatterData = sample.map((r) => {
        const xVal = typeof r[xKey] === "number" ? r[xKey] : rnd(1, 10);
        const yVal = typeof r[yKey] === "number" ? r[yKey] : rnd(30, 90);
        return [xVal, yVal, r.collected_amount || 0, r.agent || "Agent"];
      });
      const maxSize = Math.max(...scatterData.map((pt) => pt[2]), 1);
      option = {
        xAxis: {
          type: "value",
          name: xLabel || xKey,
          nameLocation: "middle",
          nameGap: 30,
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        yAxis: {
          type: "value",
          name: yLabel,
          nameLocation: "middle",
          nameGap: 65,
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        series: [
          {
            type: "scatter",
            data: scatterData.map((pt) => [pt[0], pt[1], pt[2]]),
            symbolSize:
              chartType === "bubble"
                ? (d) => Math.floor(6 + (d[2] / maxSize) * 28)
                : 8,
            itemStyle: { color: colors[0], opacity: 0.7 },
          },
        ],
      };
    } else if (chartType === "gauge") {
      const avgVal = values.length
        ? parseFloat(
            (values.reduce((s, x) => s + x, 0) / values.length).toFixed(1),
          )
        : 0;
      option = {
        series: [
          {
            type: "gauge",
            startAngle: 180,
            endAngle: 0,
            center: ["50%", "65%"],
            radius: "90%",
            min: 0,
            max: 100,
            progress: {
              show: true,
              width: 14,
              itemStyle: { color: colors[0] },
            },
            pointer: { length: "60%", width: 6 },
            axisTick: { show: false },
            splitLine: { length: 8 },
            detail: {
              valueAnimation: true,
              formatter: "{value}",
              fontSize: 18,
              offsetCenter: [0, "25%"],
            },
            data: [{ value: avgVal, name: formatAxisLabel(yKey) }],
          },
        ],
      };
    } else if (chartType === "card") {
      const numericVals = values.filter((v) => typeof v === "number");
      const sum = numericVals.reduce((a, b) => a + b, 0);
      let displayVal = "";
      if (agg === "sum")
        displayVal =
          sum >= 1000 ? `₱${(sum / 1000).toFixed(1)}k` : `₱${sum.toFixed(1)}`;
      else if (agg === "avg")
        displayVal = (
          numericVals.length ? sum / numericVals.length : 0
        ).toFixed(1);
      else if (agg === "count") displayVal = values.length.toLocaleString();
      else displayVal = (values[0] || 0).toString();
      chartEl.innerHTML = `<div style="display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;text-align:center;background:#EEF2F5;border-radius:6px;border:1px solid #dde2e6;padding:20px">
        <div style="font-size:11px;font-weight:600;text-transform:uppercase;color:#646464;letter-spacing:0.5px;margin-bottom:12px">${yKey} (${agg.toUpperCase()})</div>
        <div style="font-size:36px;font-weight:800;color:#271078">${displayVal}</div>
        <div style="font-size:10px;color:#9ca3af;margin-top:8px">Based on ${filteredData.length.toLocaleString()} records</div>
      </div>`;
      return;
    } else if (chartType === "waterfall") {
      const top10 = grouped.slice(0, 10);
      const helps = [];
      const displays = [];
      let accum = 0;
      top10.forEach((r) => {
        helps.push(accum);
        displays.push(r.v);
        accum += r.v;
      });
      option = {
        xAxis: {
          type: "category",
          name: xLabel,
          nameLocation: "middle",
          nameGap: 30,
          data: top10.map((r) => r.k),
          axisLabel: { rotate: 45, hideOverlap: true },
        },
        yAxis: {
          type: "value",
          name: yLabel,
          nameLocation: "middle",
          nameGap: 65,
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        series: [
          {
            name: "Helper",
            type: "bar",
            stack: "all",
            itemStyle: { color: "transparent", borderColor: "transparent" },
            data: helps,
          },
          {
            name: "Value",
            type: "bar",
            stack: "all",
            label: { show: true, position: "top", formatter: "{c}" },
            data: displays.map((v, i) => ({
              value: v,
              itemStyle: { color: colors[i % colors.length] },
            })),
          },
        ],
      };
    } else if (chartType === "histogram") {
      const numericVals = filteredData
        .map((r) =>
          typeof r[xKey] === "number" ? r[xKey] : parseFloat(r[xKey]),
        )
        .filter((v) => !isNaN(v));
      if (!numericVals.length) {
        chartEl.innerHTML =
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af">No numeric data for histogram</div>';
        return;
      }

      const minVal = Math.min(...numericVals);
      const maxVal = Math.max(...numericVals);
      const range = maxVal - minVal;
      const binCount = parseInt(cfg.bins || "10", 10);
      const step = range > 0 ? range / binCount : 1;
      const bins = new Array(binCount).fill(0);
      const binLabels = [];
      for (let i = 0; i < binCount; i++)
        binLabels.push(
          `${(minVal + i * step).toFixed(1)}–${(minVal + (i + 1) * step).toFixed(1)}`,
        );
      numericVals.forEach((v) => {
        let idx = Math.floor((v - minVal) / step);
        if (idx >= binCount) idx = binCount - 1;
        if (idx < 0) idx = 0;
        bins[idx]++;
      });
      option = {
        xAxis: {
          type: "category",
          name: xLabel,
          nameLocation: "middle",
          nameGap: 30,
          data: binLabels,
          axisLabel: { rotate: 45, hideOverlap: true },
        },
        yAxis: {
          type: "value",
          name: "Frequency",
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        series: [
          {
            name: "Frequency",
            type: "bar",
            barCategoryGap: "4%",
            data: bins.map((v, i) => ({
              value: v,
              itemStyle: { color: colors[i % colors.length] },
            })),
          },
        ],
      };
    } else if (chartType === "calendar") {
      const dateMap = {};
      filteredData.forEach((r) => {
        let d = r.call_date;
        if (typeof d === "string") {
          const parts = d.split("-");
          if (parts.length === 3 && parts[0].length === 2) {
            d = new Date(`${parts[2]}-${parts[1]}-${parts[0]}T12:00:00Z`);
          } else {
            d = new Date(d);
          }
        }
        if (d instanceof Date && !isNaN(d.getTime())) {
          const ymd = d.toISOString().split("T")[0];
          if (!dateMap[ymd]) dateMap[ymd] = [];
          dateMap[ymd].push(typeof r[yKey] === "number" ? r[yKey] : 1);
        }
      });
      const calData = [];
      for (const ymd in dateMap) {
        const arr = dateMap[ymd];
        let fv = 0;
        if (agg === "sum") fv = arr.reduce((a, b) => a + b, 0);
        else if (agg === "avg")
          fv = arr.reduce((a, b) => a + b, 0) / arr.length;
        else fv = arr.length;
        calData.push([ymd, parseFloat(fv.toFixed(1))]);
      }
      const years = calData.map((pt) => pt[0].split("-")[0]);
      const displayYear = [...new Set(years)][0] || "2025";
      option = {
        visualMap: {
          min: 0,
          max: Math.max(...calData.map((d) => d[1]), 10),
          calculable: true,
          orient: "horizontal",
          left: "center",
          bottom: 0,
          inRange: { color: ["#edebe9", "#271078", "#10b981"] },
        },
        calendar: {
          top: 40,
          bottom: 60,
          left: 45,
          right: 15,
          cellSize: ["auto", 11],
          range: displayYear,
          itemStyle: { borderWidth: 0.5, borderColor: gridLineClr },
        },
        series: [
          { type: "heatmap", coordinateSystem: "calendar", data: calData },
        ],
      };
    } else if (chartType === "boxplot") {
      const catMap = {};
      filteredData.forEach((r) => {
        const key =
          r[xKey] instanceof Date
            ? r[xKey].toISOString().split("T")[0]
            : r[xKey] || "Unknown";
        const val = typeof r[yKey] === "number" ? r[yKey] : parseFloat(r[yKey]);
        if (!isNaN(val)) {
          if (!catMap[key]) catMap[key] = [];
          catMap[key].push(val);
        }
      });
      const boxLabels = Object.keys(catMap).slice(0, 12);
      const boxData = boxLabels.map((cat) => {
        const vals = catMap[cat].sort((a, b) => a - b);
        if (!vals.length) return [0, 0, 0, 0, 0];
        return [
          vals[0],
          vals[Math.floor(vals.length * 0.25)],
          vals[Math.floor(vals.length * 0.5)],
          vals[Math.floor(vals.length * 0.75)],
          vals[vals.length - 1],
        ];
      });
      option = {
        xAxis: {
          type: "category",
          data: boxLabels,
          axisLabel: { rotate: 45, hideOverlap: true },
        },
        yAxis: {
          type: "value",
          name: yLabel,
          nameLocation: "middle",
          nameGap: 65,
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        series: [
          {
            type: "boxplot",
            data: boxData,
            itemStyle: { borderColor: "#271078", borderWidth: 1.5 },
          },
        ],
      };
    } else if (chartType === "wordcloud") {
      const topWords = grouped.slice(0, 35);
      if (!topWords.length) {
        chartEl.innerHTML =
          '<div style="display:flex;align-items:center;justify-content:center;height:100%;color:#9ca3af">No data for word cloud</div>';
        return;
      }
      const maxVal = Math.max(...topWords.map((w) => w.v), 1);
      const minVl = Math.min(...topWords.map((w) => w.v), 0);
      chartEl.innerHTML = `<div class="rpt-wordcloud">${topWords
        .map((w, idx) => {
          const weight =
            maxVal === minVl ? 0.5 : (w.v - minVl) / (maxVal - minVl);
          const fontSize = Math.floor(10 + weight * 22);
          return `<span class="rpt-wordcloud-item" style="font-size:${fontSize}px;color:${colors[idx % colors.length]}" title="${w.k}: ${w.v.toLocaleString()}">${w.k}</span>`;
        })
        .join("")}</div>`;
      return;
    } else if (chartType === "polar") {
      const top15 = grouped.slice(0, 15);
      option = {
        tooltip: { trigger: "item", formatter: "{b}: {c} ({d}%)" },
        legend: {
          bottom: 0,
          type: "scroll",
          itemGap: 30,
          padding: [10, 0, 0, 0],
        },
        series: [
          {
            name: formatAxisLabel(yKey),
            type: "pie",
            radius: ["20%", "70%"],
            center: ["50%", "45%"],
            roseType: "radius",
            itemStyle: { borderRadius: 4 },
            data: top15.map((r, i) => ({
              name: r.k,
              value: r.v,
              itemStyle: { color: colors[i % colors.length] },
            })),
          },
        ],
      };
    } else if (chartType === "hbar") {
      option = {
        xAxis: {
          type: "value",
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        yAxis: {
          type: "category",
          data: labels.slice(0, 12).reverse(),
          axisLine: { lineStyle: { color: gridLineClr } },
        },
        series: [
          {
            name: formatAxisLabel(yKey),
            type: "bar",
            barWidth: 14,
            data: values
              .slice(0, 12)
              .reverse()
              .map((v, i) => ({
                value: v,
                itemStyle: { color: colors[i % colors.length] },
              })),
          },
        ],
      };
    } else if (chartType === "doublebar") {
      const legendKey =
        (cfg.group_by || cfg.legend) && (cfg.group_by || cfg.legend) !== "none"
          ? cfg.group_by || cfg.legend
          : "status";
      const grps = {};
      const legendVals = new Set();
      filteredData.forEach((r) => {
        const xVal = getVal(r, xKey);
        const legVal = getVal(r, legendKey);
        legendVals.add(legVal);
        if (!grps[xVal]) grps[xVal] = {};
        if (!grps[xVal][legVal]) grps[xVal][legVal] = [];
        grps[xVal][legVal].push(typeof r[yKey] === "number" ? r[yKey] : 0);
      });
      const xL = Object.keys(grps);
      const legArr = Array.from(legendVals);
      const totalData = xL.map((x) => {
        const all = [];
        legArr.forEach((leg) => {
          if (grps[x][leg]) all.push(...grps[x][leg]);
        });
        return agg === "count"
          ? all.length
          : agg === "sum"
            ? all.reduce((a, b) => a + b, 0)
            : agg === "avg" && all.length
              ? all.reduce((a, b) => a + b, 0) / all.length
              : 0;
      });
      const series = [
        {
          name: `Total`,
          type: "bar",
          barGap: "-100%",
          barWidth: "60%",
          z: 1,
          itemStyle: { color: colors[0], opacity: 0.4 },
          data: totalData.map((v) => parseFloat(v.toFixed(2))),
        },
      ];
      legArr.forEach((leg, i) => {
        const data = xL.map((x) => {
          const arr = grps[x][leg] || [];
          return agg === "count"
            ? arr.length
            : agg === "sum"
              ? arr.reduce((a, b) => a + b, 0)
              : 0;
        });
        series.push({
          name: leg,
          type: "bar",
          stack: "bd",
          barWidth: "40%",
          z: 2,
          itemStyle: { color: colors[(i + 1) % colors.length] },
          data: data.map((v) => parseFloat(v.toFixed(2))),
        });
      });
      option = {
        tooltip: { trigger: "axis", axisPointer: { type: "shadow" } },
        legend: {
          bottom: 0,
          type: "scroll",
          itemGap: 30,
          padding: [10, 0, 0, 0],
        },
        xAxis: {
          type: "category",
          data: xL,
          axisLabel: { rotate: xL.length > 5 ? 45 : 0, hideOverlap: true },
        },
        yAxis: {
          type: "value",
          splitLine: { lineStyle: { color: gridLineClr } },
        },
        series,
      };
    } else if (chartType === "multiaxis") {
      const metrics = cfg.axes || cfg.multiMetrics || [];

      const baselineGroup = groupData(
        filteredData,
        xKey,
        "count",
        "count",
        timeGrp,
      );
      const masterXLabels = baselineGroup.map((r) => r.k);

      const yAxisArray = [];
      const seriesArray = [];
      const legendData = [];

      metrics.forEach((m, idx) => {
        const mAgg = m.agg || m.aggregation || "count";
        const mType = m.type || m.chart_type || "bar";

        const grouped = groupData(filteredData, xKey, m.metric, mAgg, timeGrp);
        const map = new Map(grouped.map((r) => [r.k, r.v]));
        const data = masterXLabels.map((k) => {
          let v = map.get(k) || 0;
          return parseFloat(v.toFixed(2));
        });

        const prettyName =
          m.metric.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase()) +
          " (" +
          mAgg +
          ")";
        legendData.push(prettyName);

        const position = idx % 2 === 0 ? "left" : "right";
        const offset = idx > 1 ? Math.floor(idx / 2) * 50 : 0;

        yAxisArray.push({
          type: "value",
          position: position,
          offset: offset,
          splitLine: { show: idx === 0, lineStyle: { color: gridLineClr } },
          axisLine: {
            show: true,
            lineStyle: { color: colors[idx % colors.length] },
          },
          axisLabel: { color: colors[idx % colors.length] },
        });

        seriesArray.push({
          name: prettyName,
          type: mType,
          yAxisIndex: idx,
          data: data,
          smooth: true,
          areaStyle: mType === "area" ? { opacity: 0.25 } : undefined,
          itemStyle: { color: colors[idx % colors.length] },
        });
      });

      const maxRightOffset =
        metrics.length > 1 ? Math.floor((metrics.length - 1) / 2) * 50 : 0;
      const maxLeftOffset =
        metrics.length > 2 ? Math.floor((metrics.length - 2) / 2) * 50 : 0;

      option = {
        tooltip: { trigger: "axis", axisPointer: { type: "cross" } },
        legend: {
          data: legendData,
          bottom: 0,
          textStyle: { color: textClr },
          type: "scroll",
          itemGap: 30,
          padding: [10, 0, 0, 0],
        },
        grid: {
          ...baseConfig.grid,
          right: 16 + maxRightOffset,
          left: Math.max(16, 50 + maxLeftOffset),
        },
        xAxis: [
          {
            type: "category",
            data: masterXLabels,
            axisPointer: { type: "shadow" },
            axisLabel: {
              rotate: masterXLabels.length > 5 ? 45 : 0,
              hideOverlap: true,
              overflow: "truncate",
              width: 90,
            },
          },
        ],
        yAxis: yAxisArray,
        series: seriesArray,
      };
    } else {
      // bar / line / area
      const isLine = chartType === "line" || chartType === "area";
      const legendKey =
        (cfg.group_by || cfg.legend) && (cfg.group_by || cfg.legend) !== "none"
          ? cfg.group_by || cfg.legend
          : null;
      if (legendKey) {
        const grps = {};
        const legendVals = new Set();
        filteredData.forEach((r) => {
          const xVal = getVal(r, xKey);
          const legVal = getVal(r, legendKey);
          legendVals.add(legVal);
          if (!grps[xVal]) grps[xVal] = {};
          if (!grps[xVal][legVal]) grps[xVal][legVal] = [];
          grps[xVal][legVal].push(typeof r[yKey] === "number" ? r[yKey] : 0);
        });
        const xL = Object.keys(grps);
        const legArr = Array.from(legendVals);
        const series = legArr.map((leg, i) => ({
          name: leg,
          type: isLine ? "line" : "bar",
          stack: "total",
          smooth: true,
          data: xL.map((x) => {
            const arr = grps[x][leg] || [];
            return agg === "count"
              ? arr.length
              : agg === "sum"
                ? parseFloat(arr.reduce((a, b) => a + b, 0).toFixed(2))
                : agg === "avg" && arr.length
                  ? parseFloat(
                      (arr.reduce((a, b) => a + b, 0) / arr.length).toFixed(2),
                    )
                  : 0;
          }),
          areaStyle: chartType === "area" ? { opacity: 0.25 } : undefined,
          itemStyle: { color: colors[i % colors.length] },
        }));
        option = {
          tooltip: { trigger: "axis" },
          legend: {
            bottom: 0,
            type: "scroll",
            itemGap: 30,
            padding: [10, 0, 0, 0],
          },
          xAxis: {
            type: "category",
            name: xL.length === 1 && xL[0] === xLabel ? "" : xLabel,
            nameLocation: "middle",
            nameGap: 30,
            data: xL,
            axisLabel: { rotate: xL.length > 5 ? 45 : 0, hideOverlap: true },
          },
          yAxis: {
            type: "value",
            name: yLabel,
            nameLocation: "middle",
            nameGap: 65,
            splitLine: { lineStyle: { color: gridLineClr } },
          },
          series,
        };
      } else {
        option = {
          xAxis: {
            type: "category",
            name: labels.length === 1 && labels[0] === xLabel ? "" : xLabel,
            nameLocation: "middle",
            nameGap: 30,
            data: labels,
            axisLabel: {
              rotate: labels.length > 5 ? 45 : 0,
              hideOverlap: true,
            },
          },
          yAxis: {
            type: "value",
            name: yLabel,
            nameLocation: "middle",
            nameGap: 65,
            splitLine: { lineStyle: { color: gridLineClr } },
          },
          series: [
            {
              name: formatAxisLabel(yKey),
              type: isLine ? "line" : "bar",
              smooth: true,
              data: values,
              itemStyle: { color: colors[0] },
              areaStyle: chartType === "area" ? { opacity: 0.25 } : undefined,
            },
          ],
        };
      }
    }

    chart.setOption({ ...baseConfig, ...option });

    const resizeObs = new ResizeObserver(() => chart.resize());
    resizeObs.observe(chartRef.current);

    return () => {
      resizeObs.disconnect();
      chart.dispose();
    };
  }, [report, Loading]);

  return (
    <div>
      <Header />
      <NavBar />

      <div className="container-fluid" style={{ background: "#EEF2F5" }}>
        <div className="my-call bg-white">
          <div
            className="side-panel-with-table clearfix flex"
            style={{ display: "flex" }}
          >
            <ReportNav />
            <div className="call-table" style={{ marginBottom: "80px" }}>
              <div
                className="my-calls-column"
                style={{ padding: "15px 0px 0px 15px" }}
              >
                <div className="calls-top-pannel">
                  <div className="row">
                    <div className="flex justify-between items-center px-6">
                      <div className="pannel-heading clearfix flex items-center">
                        <div className="pannel-heading-icon">
                          <i
                            className="fa fa-file-text-o"
                            aria-hidden="true"
                          ></i>
                        </div>
                        <div className="pannel-heading-info">
                          <h3>My Reports</h3>
                          <div className="">
                            Name: {report?.report_name || "( My report name)"}
                          </div>
                        </div>
                      </div>

                      <div className="mr-8">
                        <button
                          className="px-5 py-3 mb-4 rounded-lg font-semibold hover:text-white text-gray-700 bg-white run-my-report hover:border-0"
                          onClick={() => history.goBack()}
                        >
                          Saved Reports
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              {Loading ? (
                <div
                  className="flex justify-center items-center"
                  style={{ height: "100vh" }}
                >
                  {" "}
                  <ClipLoader color="#2056FF" size="50px" />
                </div>
              ) : (
                <div>
                  {report?.data?.summary?.metrics && (
                    <div
                      className="rpt-kpi-row"
                      style={{ marginBottom: "20px", padding: "0 15px" }}
                    >
                      {report.data.summary.metrics
                        .filter((m) => m.key !== "ptp_count")
                        .map((m, idx) => {
                          const colors = [
                            "#271078",
                            "#10b981",
                            "#f59e0b",
                            "#8b5cf6",
                            "#e25858",
                            "#14b8a6",
                            "#3b82f6",
                          ];
                          return (
                            <div
                              key={m.key}
                              className="rpt-kpi-card"
                              style={{
                                borderTopColor: colors[idx % colors.length],
                              }}
                            >
                              <div className="rpt-kpi-label">{m.label}</div>
                              <div className="rpt-kpi-value">{m.value}</div>
                              {m.change && (
                                <div
                                  className={`rpt-kpi-delta ${m.change.includes("↓") ? "down" : "up"}`}
                                >
                                  {m.change}
                                </div>
                              )}
                            </div>
                          );
                        })}
                    </div>
                  )}

                  <div
                    className="dropdowns-container"
                    style={{
                      border: "1px solid #ccc",
                      borderRadius: "8px",
                      padding: "10px",
                    }}
                  >
                    <div
                      className="grid grid-cols-1 mt-8 px-4 role"
                      style={{
                        height: "500px",
                        width: "100%",
                      }}
                    >
                      <div>
                        <div className="">
                          <div
                            ref={chartRef}
                            style={{ width: "100%", height: "450px" }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

export default Savedreportdetail;
